import {
  format as formatDate,
  type IntlFormatDistanceOptions,
  intlFormatDistance,
} from 'date-fns';
import { isDate, isString } from 'lodash-es';

import { DEFAULT_DATE_LOCALE, DEFAULT_DATE_TIME_FORMAT } from './date_formats';

// This function is used to parse a date from a string and handle default timezone.
export const localDate = (rawDateTime?: string | Date): Date => {
  if (!rawDateTime) {
    return new Date();
  }
  if (isDate(rawDateTime)) {
    return new Date(rawDateTime);
  }

  // If date is in ISO format with timezone, we can parse it directly.
  if (isString(rawDateTime)) {
    // Following timezone qualifiers are supported:
    // <time>Z
    // <time>±hh:mm
    // <time>±hhmm
    // <time>±hh
    if (
      /Z$/.test(rawDateTime) ||
      /[+-][0-2][0-9](:?[0-6][0-9])?$/.test(rawDateTime)
    ) {
      return new Date(rawDateTime);
    }
  }

  // Other formats like: 'yyyy-MM-dd', 'yyyy-MM-dd HH:mm:ss'.
  let dateTimeString = rawDateTime;

  // Change yyyy-MM-dd HH:mm:ss to yyyy-MM-ddTHH:mm:ss otherwise Safari doesn't know how to parse it.
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateTimeString)) {
    dateTimeString = dateTimeString.replace(' ', 'T');
  }

  // Append a zero time if we got only date, it's necessary to
  // force a zero timezone below.
  if (!/[0-2]?[0-9]:[0-6][0-9]/.test(dateTimeString)) {
    dateTimeString += 'T00:00';
  }

  // We force the zero timezone so browsers parse it as a local time properly.
  // Without a Z timezone Safari and Chrome parse it differently.
  return new Date(`${dateTimeString}Z`);
};

export const format = (
  date: Date | number,
  dateFormat: string,
): string | null => {
  const dateFormatWithFallback = dateFormat || DEFAULT_DATE_TIME_FORMAT;
  let result = null;
  try {
    result = formatDate(date, dateFormatWithFallback);
  } catch {}
  return result;
};

export const formatLocalDate = (
  date: string | Date | undefined,
  dateFormat = DEFAULT_DATE_TIME_FORMAT,
): string | null => format(localDate(date), dateFormat);

/** Return *date* formatted to a relative time string, e.g. "2 days ago", "in 3 months" */
export function formatRelativeDate(date: string) {
  const options: IntlFormatDistanceOptions = {
    numeric: 'always',
    locale: DEFAULT_DATE_LOCALE,
    style: 'long',
  };

  let result: string | null;
  try {
    // https://github.com/date-fns/date-fns/issues/3490
    // in Safari 13 there is no support of Intl.RelativeTimeFormat
    // which is used inside intlFormatDistance so we have to catch
    // the exception and fallback to regular formatting of the date
    result = intlFormatDistance(localDate(date), Date.now(), options);

    // We don't want to show "x quarters ago", so forcing months.
    if (result.includes('quarter') || result.includes('qtr')) {
      result = intlFormatDistance(localDate(date), Date.now(), {
        ...options,
        unit: 'month',
      });
    }
  } catch {
    result = formatLocalDate(date, DEFAULT_DATE_TIME_FORMAT);
  }
  return result;
}
